import { Controller } from "@hotwired/stimulus"

// Contrôleur pour afficher dynamiquement un warning si le délai minimum entre séances n'est pas respecté
export default class extends Controller {
  static values = { 
    defaultHours: { type: Number, default: 24 },     // 24 heures par défaut
    firstSessionHours: { type: Number, default: 48 } // 48 heures pour une première séance
  }
  static targets = [
    "clientField",
    "startDateField", 
    "startTimeField",
    "warningContainer"
  ]

  connect() {
    console.log("MinimumDelayWarning controller connected")
    
    // Vérifier immédiatement au chargement si un warning doit être affiché
    this.checkDelayWarning()
    
    // Vérifier plusieurs fois avec des délais croissants pour s'assurer que les champs sont bien remplis
    // (utile quand le formulaire a des valeurs pré-remplies ou des combobox qui mettent du temps à se remplir)
    const delays = [50, 150, 300, 500, 1000]
    delays.forEach(delay => {
      setTimeout(() => {
        this.checkDelayWarning()
      }, delay)
    })
    
    // Écouter les événements de changement dans tout le formulaire
    this.element.addEventListener('change', (event) => {
      console.log("Form change detected:", event.target.name, event.target.value)
      setTimeout(() => this.checkDelayWarning(), 50)
    })

    // Écouter les événements d'input pour une réactivité plus rapide
    this.element.addEventListener('input', (event) => {
      if (event.target.type === 'date' || event.target.type === 'time') {
        console.log("Date/time input detected:", event.target.name, event.target.value)
        setTimeout(() => this.checkDelayWarning(), 50)
      }
    })

    // Écouter spécifiquement les changements sur le champ client caché
    const clientHiddenField = this.element.querySelector('#client-combobox-hidden-input')
    if (clientHiddenField) {
      clientHiddenField.addEventListener('change', (event) => {
        console.log("Client hidden field changed:", event.target.value)
        setTimeout(() => this.checkDelayWarning(), 100)
      })
    }
  }

  checkDelayWarning() {
    console.log("Checking delay warning...")
    
    const clientId = this.getClientId()
    const startDateTime = this.getStartDateTime()

    if (!clientId || !startDateTime) {
      console.log("Missing client or start time, hiding warning")
      this.hideWarning()
      return
    }

    console.log(`Checking for client ${clientId} at ${startDateTime}`)

    // Faire un appel AJAX pour vérifier le délai minimum
    this.fetchLastSessionInfo(clientId, startDateTime)
  }

  getClientId() {
    // Méthode 1: Utiliser le target défini
    if (this.hasClientFieldTarget) {
      const value = this.clientFieldTarget.value
      console.log(`Client ID from target: ${value}`)
      if (value) return value
    }

    // Méthode 2: Chercher le champ caché client_id (utilisé par les combobox)
    const hiddenClientField = this.element.querySelector('input[name*="client_id"][type="hidden"]')
    if (hiddenClientField && hiddenClientField.value) {
      const value = hiddenClientField.value
      console.log(`Client ID from hidden field: ${value}`)
      return value
    }

    // Méthode 3: Chercher le select client_id standard
    const selectClientField = this.element.querySelector('select[name*="client_id"]')
    if (selectClientField && selectClientField.value) {
      const value = selectClientField.value
      console.log(`Client ID from select: ${value}`)
      return value
    }

    // Méthode 4: Fallback général sur tous les champs client_id
    const clientField = this.element.querySelector('[name*="client_id"]')
    if (clientField) {
      const value = clientField.value
      console.log(`Client ID from fallback: ${value}`)
      return value
    }

    console.log("No client ID found")
    return null
  }

  getStartDateTime() {
    let date = null
    let time = null

    // Récupérer la date
    if (this.hasStartDateFieldTarget) {
      date = this.startDateFieldTarget.value
    } else {
      const dateField = this.element.querySelector('[name*="start_date"]')
      if (dateField) {
        date = dateField.value
      }
    }

    // Récupérer l'heure
    if (this.hasStartTimeFieldTarget) {
      time = this.startTimeFieldTarget.value
    } else {
      const timeField = this.element.querySelector('[name*="start_time_only"]')
      if (timeField) {
        time = timeField.value
      }
    }

    if (!date || !time) {
      console.log(`Missing date (${date}) or time (${time})`)
      return null
    }

    // Combiner date et heure en format ISO
    const dateTime = `${date}T${time}:00`
    console.log(`Combined datetime: ${dateTime}`)
    return dateTime
  }

  async fetchLastSessionInfo(clientId, startDateTime) {
    try {
      const url = `/api/clients/${clientId}/last_sunbed_session_info?start_time=${encodeURIComponent(startDateTime)}`
      console.log(`Fetching: ${url}`)
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        console.warn(`API call failed: ${response.status}`)
        this.hideWarning()
        return
      }

      const data = await response.json()
      console.log("API response:", data)

      if (data.has_delay_warning || data.is_first_session || data.promo_active || data.delay_exceeded) {
        this.showInfo(data)
      } else {
        this.hideWarning()
      }

    } catch (error) {
      console.error("Error checking delay warning:", error)
      this.hideWarning()
    }
  }

  showInfo(data) {
    if (!this.hasWarningContainerTarget) {
      console.warn("No warning container target found")
      return
    }

    let htmlContent = ''

    // 1. Warning délai minimum (Prioritaire)
    if (data.has_delay_warning) {
      const nextAllowedDate = new Date(data.next_allowed_time)
      const nextAllowedFormatted = nextAllowedDate.toLocaleString('fr-FR', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })

      htmlContent += `
        <div class="bg-amber-50 border border-amber-200 rounded-md p-4 mb-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-amber-800">
                Délai minimum non respecté
              </h3>
              <div class="mt-2 text-sm text-amber-700">
                <p>Ce client a eu sa dernière séance il y a moins de ${data.min_delay_hours} heures.</p>
                <p class="mt-1">Prochaine séance : <strong>${nextAllowedFormatted}</strong></p>
              </div>
            </div>
          </div>
        </div>
      `
    }

    // 2. Remarque Première séance / > 30 jours
    if (data.is_first_session) {
      htmlContent += `
        <div class="bg-blue-50 border border-blue-200 rounded-md p-4 mb-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-blue-800">
                Première séance ou retour après > 30 jours
              </h3>
              <div class="mt-2 text-sm text-blue-700">
                <p>Ce client n'est pas venu depuis plus de 30 jours (ou c'est sa première visite).</p>
                <ul class="list-disc list-inside mt-1">
                  <li>Durée de séance réduite (-50%)</li>
                  <li>Délai avant la prochaine séance : <strong>48h minimum</strong></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      `
    }

    // 3. Remarque Promo active
    if (data.promo_active) {
      htmlContent += `
        <div class="bg-green-50 border border-green-200 rounded-md p-4 mb-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-green-800">
                Promo 2ème séance active !
              </h3>
              <div class="mt-2 text-sm text-green-700">
                <p>Le client a une séance récente payée plein tarif.</p>
                <p class="font-bold mt-1">Cette séance est à 5€.</p>
              </div>
            </div>
          </div>
        </div>
      `
    }

    // 4. Remarque Délai promo dépassé
    if (data.delay_exceeded && !data.promo_active) {
      htmlContent += `
        <div class="bg-gray-50 border border-gray-200 rounded-md p-4 mb-3">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-gray-800">
                Délai promo 2ème séance dépassé
              </h3>
              <div class="mt-2 text-sm text-gray-700">
                <p>Plus de 7 jours se sont écoulés depuis la dernière séance payée.</p>
                <p>La promo à 5€ n'est plus applicable.</p>
              </div>
            </div>
          </div>
        </div>
      `
    }

    this.warningContainerTarget.innerHTML = htmlContent
    this.warningContainerTarget.classList.remove('hidden')
    console.log("Info shown")
  }

  hideWarning() {
    if (this.hasWarningContainerTarget) {
      this.warningContainerTarget.classList.add('hidden')
      this.warningContainerTarget.innerHTML = ''
      console.log("Warning hidden")
    }
  }
}
